import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { ScrollProgress, ScrollToTop } from '@/components/ScrollAnimations';
import AnimatedPageHero from '@/components/AnimatedPageHero';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { clients, strategicCustomers, siteConfig } from '@/data/siteData';
import { ArrowRight, Building2, Users, Star, CheckCircle2 } from 'lucide-react';

// Generate initials for company logo placeholder
const getInitials = (name) => {
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
};

// Company Logo Component with styled initials fallback
const CompanyLogo = ({ name, logo, size = 'md' }) => {
  const sizeClasses = {
    sm: { container: 'w-16 h-16', text: 'text-sm', img: 'h-12' },
    md: { container: 'w-24 h-24', text: 'text-xl', img: 'h-16' },
    lg: { container: 'w-32 h-32', text: 'text-2xl', img: 'h-20' },
  };
  
  const getInitials = (companyName) => {
    return companyName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  };
  
  // Color variations based on name for visual variety
  const colors = [
    'from-[#0066CC] to-[#004499]',
    'from-[#0077CC] to-[#0055AA]',
    'from-[#0088DD] to-[#0066BB]',
    'from-[#0055AA] to-[#003377]',
  ];
  const colorIndex = name.charCodeAt(0) % colors.length;
  
  if (logo) {
    return (
      <div className={`${sizeClasses[size].container} flex items-center justify-center`}>
        <img 
          src={logo} 
          alt={name}
          className={`${sizeClasses[size].img} w-auto object-contain`}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div className={`${sizeClasses[size].container} rounded-2xl bg-gradient-to-br ${colors[colorIndex]} items-center justify-center shadow-lg hidden`}>
          <span className={`text-white font-bold ${sizeClasses[size].text}`}>{getInitials(name)}</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`${sizeClasses[size].container} rounded-2xl bg-gradient-to-br ${colors[colorIndex]} flex items-center justify-center shadow-lg`}>
      <span className={`text-white font-bold ${sizeClasses[size].text}`}>{getInitials(name)}</span>
    </div>
  );
};

export const ClientsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Our Clients - Felix Infotech | Trusted by 100+ Enterprise Leaders</title>
        <meta name="description" content="Felix Infotech is trusted by 100+ enterprise clients across banking, insurance, manufacturing, healthcare, and technology sectors in India, Dubai & Singapore." />
        <meta name="keywords" content="Felix Infotech clients, enterprise IT clients India, banking IT solutions, manufacturing IT services, healthcare IT, insurance technology partner" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://felixinfotech.com/clients" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Our Clients - Felix Infotech | Trusted by Industry Leaders" />
        <meta property="og:description" content="Serving 100+ enterprise clients across banking, insurance, manufacturing, healthcare, and technology sectors." />
        <meta property="og:url" content="https://felixinfotech.com/clients" />
        <meta property="og:site_name" content="Felix Infotech" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Felix Infotech Clients" />
        <meta name="twitter:description" content="Trusted by 100+ enterprise clients across India and Middle East." />
      </Helmet>

      <ScrollProgress />
      <Navbar />

      <main>
        <AnimatedPageHero
          title="Trusted by Industry Leaders"
          subtitle="Our Clients"
          description="Serving India's leading enterprises across banking, insurance, manufacturing, and technology sectors for over 22 years."
          badge="Clients"
          animationType="cloud"
          gradient="from-[#0066CC] to-[#004499]"
          breadcrumbs={[{ label: 'Clients' }]}
        />

        {/* Stats */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: siteConfig.clients, label: 'Enterprise Clients' },
                { value: siteConfig.experience, label: 'Years of Trust' },
                { value: '8', label: 'Office Locations' },
                { value: '99%', label: 'Client Retention' },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <p className="text-3xl md:text-4xl font-bold text-[#0066CC]">{stat.value}</p>
                  <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Strategic Customers */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="flex items-center justify-center gap-2 mb-4">
                <Star className="w-5 h-5 text-[#0066CC]" />
                <p className="text-xs font-semibold tracking-widest uppercase text-[#0066CC]">Strategic Customers</p>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Key Accounts</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Long-term partnerships with India's leading organizations across multiple sectors.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {strategicCustomers.map((customer, index) => (
                <motion.div
                  key={customer.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="rounded-xl p-6 hover:shadow-lg hover:border-[#0066CC]/20 transition-all flex flex-col items-center justify-center min-h-[160px]"
                >
                  <CompanyLogo name={customer.name} logo={customer.logo} size="lg" />
                  <h3 className="font-semibold text-gray-900 text-center text-sm mt-3">{customer.name}</h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries We Serve */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Our expertise spans across diverse industry verticals.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { name: 'Banking & Finance', icon: Building2 },
                { name: 'Insurance', icon: CheckCircle2 },
                { name: 'Manufacturing', icon: Building2 },
                { name: 'Healthcare', icon: Users },
                { name: 'IT & Software', icon: Building2 },
                { name: 'Telecom', icon: Building2 },
                { name: 'Government', icon: Building2 },
                { name: 'Education', icon: Users },
              ].map((industry, index) => (
                <motion.div
                  key={industry.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#0066CC]/10 flex items-center justify-center mx-auto mb-3">
                    <industry.icon className="w-6 h-6 text-[#0066CC]" />
                  </div>
                  <h3 className="font-medium text-gray-900 text-sm">{industry.name}</h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* All Clients */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Valued Clients</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                A selection of organizations that trust Felix Infotech for their technology needs.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {clients.map((client, index) => (
                <motion.div
                  key={client.name}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.02 }}
                  className="rounded-xl p-6 flex flex-col items-center justify-center hover:shadow-lg transition-all min-h-[160px]"
                >
                  {client.logo ? (
                    <img 
                      src={client.logo} 
                      alt={client.name}
                      className="h-16 w-auto max-w-[120px] object-contain mb-3"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={`${client.logo ? 'hidden' : 'flex'} w-16 h-16 rounded-xl bg-gradient-to-br from-[#0066CC] to-[#004499] items-center justify-center mb-3 shadow-sm`}>
                    <span className="text-white font-bold text-lg">{client.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-700 text-center">{client.name}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#0066CC]">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Join Our Growing Client Base</h2>
            <p className="text-white/80 max-w-2xl mx-auto mb-8">
              Discover how Felix Infotech can help transform your business with innovative IT solutions.
            </p>
            <Link to="/contact">
              <Button className="bg-white text-[#0066CC] hover:bg-gray-100 font-semibold">
                Get Started <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        <ContactForm />
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default ClientsPage;
